<template>
  <modal
    name="edit-navigation-modal"
    class="edit-navigation-modal"
    :width="920"
    :height="640"
    scrollable
    :clickToClose="false"
    v-model="show"
  >
    <div class="edit-nav-container">
      <div class="modal-title border-bottom position-relative">
        Edit navigation
        <button class="close nav-edit-close" aria-label="Close" @click="cancel">
          &times;
        </button>
      </div>

      <div class="edit-nav-body">
        <p class="text-muted small">
          Tabs in <strong>Navigation bar</strong> appear directly in the Client
          Profile. Everything under <strong>More</strong> moves into a dropdown.
          This applies to everyone in this environment - each person still only
          sees the tabs their role and permissions allow.
          <strong>Profile</strong> is always the tab a client opens on, wherever
          you place it.
        </p>

        <div class="row">
          <div class="col-12 col-md-6 mb-3 mb-md-0">
            <div class="section-eyebrow mb-2">
              Navigation bar
              <span class="text-muted font-weight-normal">
                · {{ bar.length }}
              </span>
            </div>
            <transition-group
              tag="div"
              name="nav-reorder"
              class="nav-edit-list"
            >
              <div
                class="nav-edit-row"
                :class="{ 'nav-edit-row-moved': highlightedKey === item.key }"
                v-for="(item, index) in bar"
                :key="item.key"
                :data-key="item.key"
                ref="barRow"
              >
                <icon
                  v-if="item.icon"
                  :type="item.icon"
                  class="mr-2 text-muted"
                />
                <span class="nav-edit-label">{{ labelFor(item) }}</span>
                <!-- v-tooltip, not a title attribute: main.js opens it on click below
                     768px, so it works on touch. -->
                <span
                  v-if="isLocked(item)"
                  class="badge badge-light ml-2 pointer-cursor"
                  v-tooltip.top="
                    'Profile always stays in the navigation bar, and is the tab a client opens on.'
                  "
                >
                  required
                </span>
                <span class="nav-edit-actions ml-auto">
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="index === 0"
                    :title="`Move ${labelFor(item)} up`"
                    :aria-label="`Move ${labelFor(item)} up`"
                    @click="move(bar, index, -1)"
                  >
                    <i class="fas fa-arrow-up"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="index === bar.length - 1"
                    :title="`Move ${labelFor(item)} down`"
                    :aria-label="`Move ${labelFor(item)} down`"
                    @click="move(bar, index, 1)"
                  >
                    <i class="fas fa-arrow-down"></i>
                  </button>
                  <button
                    v-if="!isLocked(item)"
                    class="btn btn-sm btn-outline-blue"
                    :title="`Move ${labelFor(item)} to More`"
                    :aria-label="`Move ${labelFor(item)} to More`"
                    @click="toMore(index)"
                  >
                    <i class="fas fa-arrow-right"></i>
                  </button>
                </span>
              </div>
            </transition-group>
          </div>

          <div class="col-12 col-md-6">
            <div class="section-eyebrow mb-2">
              More
              <span class="text-muted font-weight-normal">
                · {{ more.length }}
              </span>
            </div>
            <p class="text-muted small nav-edit-empty" v-if="!more.length">
              Nothing under More. Every tab shows directly in the navigation
              bar.
            </p>
            <transition-group
              tag="div"
              name="nav-reorder"
              class="nav-edit-list"
            >
              <div
                class="nav-edit-row"
                :class="{ 'nav-edit-row-moved': highlightedKey === item.key }"
                v-for="(item, index) in more"
                :key="item.key"
                :data-key="item.key"
                ref="moreRow"
              >
                <span class="nav-edit-actions mr-2">
                  <button
                    class="btn btn-sm btn-outline-blue"
                    :title="`Move ${labelFor(item)} to the navigation bar`"
                    :aria-label="`Move ${labelFor(item)} to the navigation bar`"
                    @click="toBar(index)"
                  >
                    <i class="fas fa-arrow-left"></i>
                  </button>
                </span>
                <icon
                  v-if="item.icon"
                  :type="item.icon"
                  class="mr-2 text-muted"
                />
                <span class="nav-edit-label">{{ labelFor(item) }}</span>
                <span class="nav-edit-actions ml-auto">
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="index === 0"
                    :title="`Move ${labelFor(item)} up`"
                    :aria-label="`Move ${labelFor(item)} up`"
                    @click="move(more, index, -1)"
                  >
                    <i class="fas fa-arrow-up"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="index === more.length - 1"
                    :title="`Move ${labelFor(item)} down`"
                    :aria-label="`Move ${labelFor(item)} down`"
                    @click="move(more, index, 1)"
                  >
                    <i class="fas fa-arrow-down"></i>
                  </button>
                </span>
              </div>
            </transition-group>
          </div>
        </div>
      </div>

      <div class="edit-nav-footer border-top">
        <button
          v-if="configured"
          class="btn btn-outline-danger mr-auto"
          :disabled="isSaving"
          @click="confirmReset"
        >
          Reset to system default
        </button>
        <button class="btn btn-black mr-2" :disabled="isSaving" @click="cancel">
          Cancel
        </button>
        <button
          class="btn btn-theme"
          :disabled="isSaving || !canSave"
          @click="save"
        >
          {{ isSaving ? "Saving..." : "Save changes" }}
        </button>
      </div>
    </div>
  </modal>
</template>
<script>
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";
import {
  buildClientNav,
  MANDATORY_BAR_KEYS,
  profileNavEditorLabel,
} from "./profileNavRegistry";
import { resolveProfileNav } from "./profileNavResolver";

const swalWithBootstrapButtons = Swal.mixin({
  customClass: {
    confirmButton: "btn btn-black ml-4",
    cancelButton: "btn btn-danger",
  },
  buttonsStyling: false,
});

export default {
  name: "EditNavigationModal",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      bar: [],
      more: [],
      openingBar: [],
      openingMore: [],
      highlightedKey: null,
      highlightTimer: null,
    };
  },
  computed: {
    ...mapState({
      config: (state) => state.profileNav.configs.client || null,
      isSaving: (state) => state.profileNav.isSaving,
    }),
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    configured() {
      return !!(this.config && this.config.configured === true);
    },
    registryItems() {
      return buildClientNav(this);
    },
    payload() {
      return {
        bar: this.bar.map((item) => item.key),
        more: this.more.map((item) => item.key),
      };
    },
    openingSnapshot() {
      return JSON.stringify({ bar: this.openingBar, more: this.openingMore });
    },
    isDirty() {
      return JSON.stringify(this.payload) !== this.openingSnapshot;
    },
    canSave() {
      const barKeys = this.payload.bar;
      if (!barKeys.length) return false;
      if (!MANDATORY_BAR_KEYS.every((key) => barKeys.includes(key))) {
        return false;
      }
      return this.isDirty;
    },
  },
  watch: {
    modelValue(opened) {
      if (opened) this.initialise();
    },
  },
  mounted() {
    if (this.modelValue) this.initialise();
  },
  beforeUnmount() {
    clearTimeout(this.highlightTimer);
  },
  methods: {
    ...mapActions({
      saveConfig: "profileNav/saveConfig",
      resetConfig: "profileNav/resetConfig",
    }),

    initialise() {
      const resolved = resolveProfileNav(this.registryItems, this.config);
      this.bar = [...resolved.bar];
      this.more = [...resolved.more];
      this.rebaseline();
      this.clearHighlight();
    },

    rebaseline() {
      this.openingBar = this.payload.bar;
      this.openingMore = this.payload.more;
    },

    labelFor(item) {
      return profileNavEditorLabel(item);
    },

    isLocked(item) {
      return MANDATORY_BAR_KEYS.includes(item.key);
    },

    move(list, index, direction) {
      const target = index + direction;
      if (target < 0 || target >= list.length) return;
      const destination = list === this.bar ? "bar" : "more";
      // Scroll before swapping so the swap happens in view.
      const neighbour = this.findRow(list[target], destination);
      if (neighbour && neighbour.scrollIntoView) {
        neighbour.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      const copy = [...list];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      if (destination === "bar") this.bar = copy;
      else this.more = copy;
    },

    findRow(item, destination) {
      const rows = this.$refs[destination === "bar" ? "barRow" : "moreRow"];
      return (Array.isArray(rows) ? rows : []).find(
        (el) => el && el.dataset && el.dataset.key === item.key
      );
    },

    toMore(index) {
      const item = this.bar[index];
      if (!item || this.isLocked(item)) return;
      this.bar = this.bar.filter((_, i) => i !== index);
      this.more = this.restore(this.more, item, this.openingMore);
      this.highlightMove(item, "more");
    },

    toBar(index) {
      const item = this.more[index];
      if (!item) return;
      this.more = this.more.filter((_, i) => i !== index);
      this.bar = this.restore(this.bar, item, this.openingBar);
      this.highlightMove(item, "bar");
    },

    // Slot found by opening ORDER, not raw index, so the tab still lands sensibly
    // when its neighbours have moved since.
    restore(list, item, openingKeys) {
      const target = openingKeys.indexOf(item.key);
      if (target === -1) return [...list, item];

      const at = list.findIndex((existing) => {
        const index = openingKeys.indexOf(existing.key);
        return index !== -1 && index > target;
      });

      return at === -1
        ? [...list, item]
        : [...list.slice(0, at), item, ...list.slice(at)];
    },

    // The lists stack below 768px, where the destination can be below the fold.
    // `center`, not `nearest`: the enter transform leaves `nearest` a few px short.
    highlightMove(item, destination) {
      this.highlightedKey = item.key;
      clearTimeout(this.highlightTimer);
      this.highlightTimer = setTimeout(() => {
        this.highlightedKey = null;
      }, 1200);

      this.$nextTick(() => {
        if (window.innerWidth >= 768) return; // col-md-6: lists are side by side
        const row = this.findRow(item, destination);
        if (row && row.scrollIntoView) {
          row.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },

    clearHighlight() {
      clearTimeout(this.highlightTimer);
      this.highlightTimer = null;
      this.highlightedKey = null;
    },

    save() {
      if (!this.canSave || this.isSaving) return;
      this.saveConfig({ profileType: "client", ...this.payload }).then((ok) => {
        if (!ok) return; // stay open, keep the arrangement, error already shown
        this.rebaseline();
        this.show = false;
      });
    },

    cancel() {
      if (!this.isDirty) {
        this.show = false;
        return;
      }
      swalWithBootstrapButtons
        .fire({
          title: "Discard changes?",
          text: "The arrangement you have made here will be lost.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, discard!",
          cancelButtonText: "Cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.isConfirmed) this.show = false;
        });
    },

    confirmReset() {
      if (this.isSaving) return;
      swalWithBootstrapButtons
        .fire({
          title: "Reset to system default?",
          text: "The saved arrangement will be removed. Every tab a person is allowed to see will show directly in the navigation bar again, and the More menu will disappear until a new arrangement is saved.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, reset it!",
          cancelButtonText: "Cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (!result.isConfirmed) return;
          this.resetConfig({ profileType: "client" }).then((ok) => {
            if (!ok) return; // keep the configured state, error already shown
            this.show = false;
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-nav-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  padding: 1rem 1.25rem;
}

/* Same close control as .create-task-modal in assets/scss/_user.scss: large,
   fully opaque and pinned to the corner rather than floated. Centred on the
   header so it stays put whatever the title wraps to. */
.nav-edit-close {
  font-size: 2.5rem;
  line-height: 1;
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  z-index: 10;
  float: none;
  opacity: 1;
  padding: 0 0.25rem;
  min-width: 2.75rem;
  min-height: 2.75rem;
}

.edit-nav-body {
  padding: 1rem 1.25rem;
  overflow-y: auto;
  flex: 1 1 auto;
}

.edit-nav-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
}

.section-eyebrow {
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
  color: #6c757d;
}

.nav-edit-list {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  overflow: hidden;
}

.nav-edit-row {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.5rem;
  background: #fff;
}

.nav-edit-row + .nav-edit-row {
  border-top: 1px solid #f1f3f5;
}

.nav-edit-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nav-edit-actions {
  display: flex;
  gap: 0.25rem;
  flex: none;
}

.nav-edit-empty {
  border: 1px dashed #e9ecef;
  border-radius: 0.375rem;
  padding: 0.75rem;
}

.nav-reorder-move {
  transition: transform 0.2s ease;
}

/* Cross-list moves: the tab fades into its destination. Only the entering
   side is animated - a leave transition would need the row taken out of flow
   and makes the list jump, which is worse than an instant removal. */
.nav-reorder-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.nav-reorder-enter-from {
  opacity: 0;
  transform: translateY(-0.35rem);
}

/* Brief tint on the tab that just moved, so it is obvious where it landed.
   Same treatment as .row-changed in components/group-session/AttendanceManager.vue. */
.nav-edit-row-moved {
  animation: nav-edit-moved 1.2s ease-out;
}

@keyframes nav-edit-moved {
  0%,
  55% {
    background-color: #fff3cd;
  }
  100% {
    background-color: #fff;
  }
}

/* A row leaving one list is removed at once. Without this, transition-group
   waits for the highlight animation still running on the moved row, so it
   would linger in the old list and briefly appear in both. */
.nav-reorder-leave-active {
  display: none;
  animation: none;
  transition: none;
}

@media (prefers-reduced-motion: reduce) {
  .nav-reorder-move,
  .nav-reorder-enter-active {
    transition: none;
  }
  .nav-edit-row-moved {
    animation-duration: 0.01ms;
  }
}
</style>
